import { useState, useRef, useCallback } from "react";
import {
  View,
  Text,
  Pressable,
  FlatList,
  useWindowDimensions,
  NativeSyntheticEvent,
  NativeScrollEvent,
} from "react-native";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  interpolate,
  interpolateColor,
  Extrapolation,
  type SharedValue,
} from "react-native-reanimated";
import { SafeAreaView } from "react-native-safe-area-context";
import { useRouter } from "expo-router";
import styled from "styled-components/native";
import { setItem } from "@/lib/storage";
import { colors, radius, font } from "@/lib/theme";
import StumpsIcon from "@/components/StumpsIcon";

const slides = [
  {
    emoji: "🏟️",
    title: "Your club, one app",
    body: "Availability, team selection, match fees, channels, events — everything in one place. No more juggling six different apps.",
  },
  {
    emoji: "📋",
    title: "Availability & selection",
    body: "Players mark available, the captain picks the team, everyone gets notified. Simple.",
  },
  {
    emoji: "💸",
    title: "Match fees sorted",
    body: "Fees collected before match day — not chased after. Cash, bank transfer, or card. The treasurer sees it all.",
  },
  {
    emoji: "💬",
    title: "Club channels",
    body: "Team chats, committee updates, social events — one place for everything. Important stuff doesn't get buried.",
  },
];

const SafeArea = styled(SafeAreaView)`
  flex: 1;
  background-color: ${colors.white};
`;

const SlideContainer = styled.View`
  flex: 1;
  justify-content: center;
  align-items: center;
  padding-horizontal: 32px;
`;

const Footer = styled.View`
  padding-horizontal: 24px;
  padding-bottom: 16px;
`;

const DotsRow = styled.View`
  flex-direction: row;
  justify-content: center;
  gap: 8px;
  margin-bottom: 20px;
`;

const BtnText = styled.Text`
  font-size: 16px;
  font-weight: ${font.bold};
  color: #fff;
`;

const SkipText = styled.Text`
  font-size: 14px;
  font-weight: ${font.semibold};
  color: ${colors.inkMuted};
  text-align: center;
  padding: 12px;
`;

function Slide({
  item,
  index,
  scrollX,
  width,
}: {
  item: (typeof slides)[0];
  index: number;
  scrollX: SharedValue<number>;
  width: number;
}) {
  const inputRange = [(index - 1) * width, index * width, (index + 1) * width];

  const emojiStyle = useAnimatedStyle(() => ({
    opacity: interpolate(scrollX.value, inputRange, [0, 1, 0], Extrapolation.CLAMP),
    transform: [
      { scale: interpolate(scrollX.value, inputRange, [0.5, 1, 0.5], Extrapolation.CLAMP) },
    ],
  }));

  const titleStyle = useAnimatedStyle(() => ({
    opacity: interpolate(scrollX.value, inputRange, [0, 1, 0], Extrapolation.CLAMP),
    transform: [
      { translateY: interpolate(scrollX.value, inputRange, [30, 0, -30], Extrapolation.CLAMP) },
    ],
  }));

  const bodyStyle = useAnimatedStyle(() => ({
    opacity: interpolate(scrollX.value, inputRange, [0, 1, 0], Extrapolation.CLAMP),
    transform: [
      { translateY: interpolate(scrollX.value, inputRange, [40, 0, -40], Extrapolation.CLAMP) },
    ],
  }));

  return (
    <SlideContainer style={{ width }}>
      <Animated.Text style={[{ fontSize: 56, marginBottom: 24 }, emojiStyle]}>
        {item.emoji}
      </Animated.Text>
      <Animated.Text
        style={[
          {
            fontSize: 26,
            fontWeight: "800",
            color: colors.ink,
            textAlign: "center",
            letterSpacing: -0.5,
            marginBottom: 12,
          },
          titleStyle,
        ]}
      >
        {item.title}
      </Animated.Text>
      <Animated.Text
        style={[
          {
            fontSize: 16,
            color: colors.inkSecondary,
            textAlign: "center",
            lineHeight: 24,
            maxWidth: 340,
          },
          bodyStyle,
        ]}
      >
        {item.body}
      </Animated.Text>
    </SlideContainer>
  );
}

function Dot({
  index,
  scrollX,
  width,
}: {
  index: number;
  scrollX: SharedValue<number>;
  width: number;
}) {
  const inputRange = [(index - 1) * width, index * width, (index + 1) * width];

  const style = useAnimatedStyle(() => ({
    width: interpolate(scrollX.value, inputRange, [8, 24, 8], Extrapolation.CLAMP),
    backgroundColor: interpolateColor(
      scrollX.value,
      inputRange,
      [colors.line, colors.brand, colors.line]
    ),
  }));

  return <Animated.View style={[{ height: 8, borderRadius: 4 }, style]} />;
}

const AnimatedFlatList = Animated.createAnimatedComponent(FlatList);

export default function WelcomeScreen() {
  const { width } = useWindowDimensions();
  const [activeIndex, setActiveIndex] = useState(0);
  const flatListRef = useRef<FlatList>(null);
  const scrollX = useSharedValue(0);
  const router = useRouter();

  const isLast = activeIndex === slides.length - 1;

  const handleDone = async () => {
    await setItem("hasSeenWelcome", "true");
    router.replace("/(auth)/login");
  };

  const handleNext = () => {
    if (isLast) {
      handleDone();
    } else {
      flatListRef.current?.scrollToIndex({ index: activeIndex + 1 });
    }
  };

  const onScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      const x = e.nativeEvent.contentOffset.x;
      scrollX.value = x;
      const index = Math.round(x / width);
      setActiveIndex(index);
    },
    [width]
  );

  return (
    <SafeArea>
      <FlatList
        ref={flatListRef}
        data={slides}
        horizontal
        pagingEnabled
        showsHorizontalScrollIndicator={false}
        onScroll={onScroll}
        scrollEventThrottle={16}
        keyExtractor={(_, i) => String(i)}
        initialNumToRender={slides.length}
        windowSize={slides.length}
        maxToRenderPerBatch={slides.length}
        getItemLayout={(_, index) => ({
          length: width,
          offset: width * index,
          index,
        })}
        renderItem={({ item, index }) => (
          <Slide item={item} index={index} scrollX={scrollX} width={width} />
        )}
      />

      <Footer>
        <DotsRow>
          {slides.map((_, i) => (
            <Dot key={i} index={i} scrollX={scrollX} width={width} />
          ))}
        </DotsRow>

        <Pressable
          onPress={handleNext}
          style={({ pressed }) => [
            {
              backgroundColor: colors.brand,
              borderRadius: radius.card,
              paddingVertical: 16,
              alignItems: "center" as const,
              shadowColor: colors.brand,
              shadowOffset: { width: 0, height: 4 },
              shadowOpacity: 0.25,
              shadowRadius: 14,
              elevation: 4,
            },
            pressed && { opacity: 0.9 },
          ]}
        >
          <BtnText>{isLast ? "Get started" : "Next"}</BtnText>
        </Pressable>

        <Pressable onPress={handleDone} style={{ opacity: isLast ? 0 : 1 }} disabled={isLast}>
          <SkipText>Skip</SkipText>
        </Pressable>
      </Footer>
    </SafeArea>
  );
}
